import { deleteApplicationDocument } from '@access-cms/api-client';

import type { UploadedFile } from '../../../../components/FileUpload';
import { PrimaryStep, SubStep, type WorkflowFormData } from '../../config';

export type ConditionalDocumentField =
  | 'passport'
  | 'residencyDocument'
  | 'residencySupportingDocuments'
  | 'usVisaDocument';

export function getConditionalDocuments(
  formData: WorkflowFormData | undefined,
  fields: ConditionalDocumentField[],
): UploadedFile[] {
  const uploadData = formData?.[PrimaryStep.DOCUMENT_UPLOAD]?.[SubStep.UPLOAD_DOCUMENTS] as
    | Record<string, UploadedFile[]>
    | undefined;

  return fields.flatMap((field) => uploadData?.[field] ?? []);
}

export function getConditionalDocumentFileNames(
  formData: WorkflowFormData | undefined,
  fields: ConditionalDocumentField[],
): string[] {
  const uploadData = formData?.[PrimaryStep.DOCUMENT_UPLOAD]?.[SubStep.UPLOAD_DOCUMENTS] as
    | Record<string, UploadedFile[]>
    | undefined;

  return fields.flatMap((field) => (uploadData?.[field] ?? []).map((document) => document.name));
}

export async function deleteConditionalDocuments(
  applicationId: string | null,
  documents: UploadedFile[],
): Promise<void> {
  if (!applicationId) return;

  await Promise.all(
    documents.map((document) => {
      const documentId = document.documentId ?? document.id;

      return documentId && document.uploadStatus === 'associated'
        ? deleteApplicationDocument(applicationId, documentId)
        : Promise.resolve();
    }),
  );
}

export function clearConditionalDocumentFields(
  formData: WorkflowFormData,
  fields: ConditionalDocumentField[],
): WorkflowFormData {
  const uploadData = formData[PrimaryStep.DOCUMENT_UPLOAD]?.[SubStep.UPLOAD_DOCUMENTS];
  if (!uploadData) return formData;

  return {
    ...formData,
    [PrimaryStep.DOCUMENT_UPLOAD]: {
      ...formData[PrimaryStep.DOCUMENT_UPLOAD],
      [SubStep.UPLOAD_DOCUMENTS]: {
        ...uploadData,
        ...Object.fromEntries(fields.map((field) => [field, []])),
      },
    },
  };
}
